import React from 'react';
import { useWindowSize } from 'react-use';
import { connect } from 'react-redux';
// We only need to import this type from history package as react-router doesn't re-export that.
// No need to include that to direct dependencies.
// eslint-disable-next-line import/no-extraneous-dependencies
import { Location } from 'history';
import { InjectedRouter } from 'react-router';
import { ReactRouterProvider, Params } from 'react-router-dom';
import { storeParam as storeCodeSelector } from 'ggApp/modules/router/selectors';

import { isLoggedIn as isLoggedInSelector } from 'ggApp/modules/auth/selectors';
import DisplayIf from 'ggApp/shared/components/viewport';
import LoadingModal from 'ggApp/shared/common/LoadingModal';
import MobileNavigation from 'ggApp/shared/components/navigation/Mobile';
import DashboardErrorBoundary from 'ggApp/shared/screens/NewDashboard/ErrorBoundary';
import { DashboardProvider } from 'ggApp/shared/components/NewDashboard/Context';

import { isAppUser } from 'ggApp/routes/_wrap/MobileWrapper/utils';
import { LayoutWrap, Header, FadeInOutWrap } from './styles';

export type SelectorProps = {
    isLoggedIn: boolean;
    storeCode: string;
};

export type DashboardLayoutWrapProps = {
    children: React.ReactElement;
    className?: string;
    location: Location;
    router: InjectedRouter;
    params: Params;
};

const DashboardLayoutWrap = ({
    children,
    isLoggedIn,
    className,
    location,
    router,
    storeCode,
    params,
}: DashboardLayoutWrapProps & SelectorProps): JSX.Element => {
    const { height } = useWindowSize();
    const isBusinessStore = storeCode.startsWith('business');
    const isGroverApp = isAppUser();
    const showHeader = !isGroverApp && !isBusinessStore;

    return (
        <ReactRouterProvider location={location} router={router} params={params}>
            <DashboardProvider>
                <LayoutWrap minHeight={height} className={className}>
                    <LoadingModal />
                    <DisplayIf
                        desktop
                        largeDesktop
                        widescreen
                        tablet={showHeader}
                        mobile={showHeader}
                    >
                        <Header isLoggedIn={isLoggedIn} />
                    </DisplayIf>

                    <DashboardErrorBoundary>
                        <FadeInOutWrap
                            animateRoutes={[
                                'your-tech',
                                'your-mix',
                                'your-payments',
                                'your-profile',
                                'grovercash',
                            ]}
                            location={location}
                        >
                            {children}
                        </FadeInOutWrap>
                    </DashboardErrorBoundary>

                    <DisplayIf mobile tablet>
                        {isLoggedIn && <MobileNavigation location={location} />}
                    </DisplayIf>
                </LayoutWrap>
            </DashboardProvider>
        </ReactRouterProvider>
    );
};

const enhance = connect<SelectorProps, {}, DashboardLayoutWrapProps>((state) => ({
    isLoggedIn: isLoggedInSelector(state),
    storeCode: storeCodeSelector(state),
}));

export default enhance(DashboardLayoutWrap);
